import { Injectable, inject } from '@angular/core';

import { TODO } from '@b2b/types/core/utils';

import { CalendarService } from '../calendar/calendar.service';
import { HelpersService } from '../helpers/helpers.service';
import { ResourcesService } from '../resources/resources.service';
import { FlexpayService } from '../templates/flexpay/flexpay.service';
import { TemplatesHelpersService } from '../templates/helpers/templates-helpers.service';
import { TemplatesService } from '../templates/templates.service';
import { FILTER_PORT, type IFilterPort } from '../utils/filter.port';
import { AuditTrailVersionHandlerService } from './audit-trail-version-handler.service';

export interface StorageType {
  breadcrumb: string[];
  headers: string[] | (() => string[] | undefined);
  accessor: string | null;
  handlers: {
    row: (op: TODO, original: TODO) => TODO;
    rowByCondition: (op1: TODO, op2: TODO, original: TODO, updated: TODO) => TODO;
  };
}

class Storage {
  priceLists: StorageType;
  priceList: StorageType;
  prices: StorageType;
  values: StorageType;
  addOns: StorageType;
  schedules: StorageType;
  membership: StorageType;
  addOnsCombo: StorageType;
  standard: StorageType;
  exceptions: StorageType;
  flexPays: StorageType;
  resources: StorageType;
  resourceRequests: StorageType;
  [key: string]: StorageType;

  // `config` is the owning AuditTrailConfigsService; the schedule/flexpay/resource
  // grid handlers close over it to call the row formatters and read `config.path`,
  // mirroring the legacy module-singleton `AuditTrailConfig` reference.
  constructor(config: AuditTrailConfigsService) {
    this.priceLists = {
      breadcrumb: ['Pricing', 'Price List'],
      headers: ['Ticket Type', 'Description', 'Price', 'Web', 'Pos', 'Reseller', 'Customer Rep.'],
      accessor: 'prices',
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.personType.name },
            col1: { op: op, value: original.personType.description },
            col2: { op: op, value: original.price, format: 'currency' },
            col3: {
              op: op,
              value: original.salesChannels[0].enabled,
              format: 'boolean',
            },
            col4: {
              op: op,
              value: original.salesChannels[2].enabled,
              format: 'boolean',
            },
            col5: {
              op: op,
              value: original.salesChannels[4].enabled,
              format: 'boolean',
            },
            col6: {
              op: op,
              value: original.salesChannels[1].enabled,
              format: 'boolean',
            },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0:
              original.personType.name === updated.personType.name
                ? { op: op1, value: original.personType.name }
                : { op: op2, value: updated.personType.name },
            col1:
              original.personType.description === updated.personType.description
                ? { op: op1, value: original.personType.description }
                : { op: op2, value: updated.personType.description },
            col2:
              original.price === updated.price
                ? { op: op1, value: original.price, format: 'currency' }
                : { op: op2, value: updated.price, format: 'currency' },
            col3:
              original.salesChannels[0].enabled === updated.salesChannels[0].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[0].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[0].enabled,
                    format: 'boolean',
                  },
            col4:
              original.salesChannels[2].enabled === updated.salesChannels[2].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[2].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[2].enabled,
                    format: 'boolean',
                  },
            col5:
              original.salesChannels[4].enabled === updated.salesChannels[4].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[4].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[4].enabled,
                    format: 'boolean',
                  },
            col6:
              original.salesChannels[1].enabled === updated.salesChannels[1].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[1].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[1].enabled,
                    format: 'boolean',
                  },
          };
        },
      },
    };
    this.priceList = this.priceLists;
    this.prices = this.priceLists;
    this.values = {
      breadcrumb: ['Discounts'],
      headers: ['Ticket Type', 'Description', 'Discount Amount', 'Discount Type', 'Discount Tag'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.personType.name },
            col1: { op: op, value: original.personType.description },
            col2: {
              op: op,
              value: original.value,
              format: original.discountType === 'PercentageBased' ? 'percent' : 'value',
            },
            col3: { op: op, value: original.discountType },
            col4: { op: op, value: original.tag },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0:
              original.personType.name === updated.personType.name
                ? { op: op1, value: original.personType.name }
                : { op: op2, value: updated.personType.name },
            col1:
              original.personType.description === updated.personType.description
                ? { op: op1, value: original.personType.description }
                : { op: op2, value: updated.personType.description },
            col2:
              original.value === updated.value
                ? {
                    op: op1,
                    value: original.value,
                    format: updated.discountType === 'PercentageBased' ? 'percent' : 'value',
                  }
                : {
                    op: op2,
                    value: updated.value,
                    format: updated.discountType === 'PercentageBased' ? 'percent' : 'value',
                  },
            col3:
              original.discountType === updated.discountType
                ? { op: op1, value: original.discountType }
                : { op: op2, value: updated.discountType },
            col4: original.tag === updated.tag ? { op: op1, value: original.tag } : { op: op2, value: updated.tag },
          };
        },
      },
    };
    this.addOns = {
      breadcrumb: ['Add-Ons'],
      headers: ['Name', 'Description', 'Sku', 'Price', 'Web', 'Pos', 'Reseller', 'Customer Rep.'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.name },
            col1: { op: op, value: original.description },
            col2: { op: op, value: original.sku },
            col3: { op: op, value: original.price, format: 'currency' },
            col4: {
              op: op,
              value: original.salesChannels[0].enabled,
              format: 'boolean',
            },
            col5: {
              op: op,
              value: original.salesChannels[2].enabled,
              format: 'boolean',
            },
            col6: {
              op: op,
              value: original.salesChannels[4].enabled,
              format: 'boolean',
            },
            col7: {
              op: op,
              value: original.salesChannels[1].enabled,
              format: 'boolean',
            },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0: original.name === updated.name ? { op: op1, value: original.name } : { op: op2, value: updated.name },
            col1:
              original.description === updated.description
                ? { op: op1, value: original.description }
                : { op: op2, value: updated.description },
            col2: original.sku === updated.sku ? { op: op1, value: original.sku } : { op: op2, value: updated.sku },
            col3:
              original.price === updated.price
                ? { op: op1, value: original.price, format: 'currency' }
                : { op: op2, value: updated.price, format: 'currency' },
            col4:
              original.salesChannels[0].enabled === updated.salesChannels[0].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[0].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[0].enabled,
                    format: 'boolean',
                  },
            col5:
              original.salesChannels[2].enabled === updated.salesChannels[2].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[2].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[2].enabled,
                    format: 'boolean',
                  },
            col6:
              original.salesChannels[4].enabled === updated.salesChannels[4].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[4].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[4].enabled,
                    format: 'boolean',
                  },
            col7:
              original.salesChannels[1].enabled === updated.salesChannels[1].enabled
                ? {
                    op: op1,
                    value: original.salesChannels[1].enabled,
                    format: 'boolean',
                  }
                : {
                    op: op2,
                    value: updated.salesChannels[1].enabled,
                    format: 'boolean',
                  },
          };
        },
      },
    };
    this.schedules = {
      breadcrumb: ['Schedules'],
      headers: ['Name', 'Effective', 'Occurs', 'Start', 'Duration', 'Channels', 'Price List'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO & { priceLists: any[] }) {
          var _original = config.scheduleFormatter(original, 'original');
          return {
            col0: { op: op, value: _original.name },
            col1: { op: op, value: _original.effectiveDates },
            col2: { op: op, value: _original.repeatCycle },
            col3: { op: op, value: _original.startTime },
            col4: { op: op, value: _original.duration },
            col5: { op: op, value: _original.salesChannels },
            col6: { op: op, value: _original.priceList },
          };
        },
        rowByCondition: function (
          op1: TODO,
          op2: TODO,
          original: TODO & { priceLists: any[] },
          updated: TODO & { priceLists: any[] },
        ) {
          var _original = config.scheduleFormatter(original, 'original'),
            _updated = config.scheduleFormatter(updated, 'updated');
          return {
            col0:
              _original.name === _updated.name ? { op: op1, value: _original.name } : { op: op2, value: _updated.name },
            col1:
              _original.effectiveDates === _updated.effectiveDates
                ? { op: op1, value: _original.effectiveDates }
                : { op: op2, value: _updated.effectiveDates },
            col2:
              _original.repeatCycle === _updated.repeatCycle
                ? { op: op1, value: _original.repeatCycle }
                : { op: op2, value: _updated.repeatCycle },
            col3:
              _original.startTime === _updated.startTime
                ? { op: op1, value: _original.startTime }
                : { op: op2, value: _updated.startTime },
            col4:
              _original.duration === _updated.duration
                ? { op: op1, value: _original.duration }
                : { op: op2, value: _updated.duration },
            col5:
              _original.salesChannels === _updated.salesChannels
                ? { op: op1, value: _original.salesChannels }
                : { op: op2, value: _updated.salesChannels },
            col6:
              _original.priceList === _updated.priceList
                ? { op: op1, value: _original.priceList }
                : { op: op2, value: _updated.priceList },
          };
        },
      },
    };
    this.membership = {
      breadcrumb: ['Pricing', 'Members'],
      headers: ['Membership Category', 'Discount'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.category.name },
            col1: { op: op, value: original.discount.name },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0:
              original.category.name === updated.category.name
                ? { op: op1, value: original.category.name }
                : { op: op2, value: updated.category.name },
            col1:
              original.discount.name === updated.discount.name
                ? { op: op1, value: original.discount.name }
                : { op: op2, value: updated.discount.name },
          };
        },
      },
    };
    this.addOnsCombo = {
      breadcrumb: ['Combo Add Ons'],
      headers: ['Add On', 'Add On Id', 'Price', 'Price Split', 'Quantity', 'Quantity Type'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.addOn },
            col1: { op: op, value: original.addOnId },
            col2: { op: op, value: original.price },
            col3: { op: op, value: original.priceSplit },
            col4: { op: op, value: original.quantity },
            col5: { op: op, value: original.quantityType },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0:
              original.addOn === updated.addOn ? { op: op1, value: original.addOn } : { op: op2, value: updated.addOn },
            col1:
              original.addOnId === updated.addOnId
                ? { op: op1, value: original.addOnId }
                : { op: op2, value: updated.addOnId },
            col2:
              original.price === updated.price ? { op: op1, value: original.price } : { op: op2, value: updated.price },
            col3:
              original.priceSplit === updated.priceSplit
                ? { op: op1, value: original.priceSplit }
                : { op: op2, value: updated.priceSplit },
            col4:
              original.quantity === updated.quantity
                ? { op: op1, value: original.quantity }
                : { op: op2, value: updated.quantity },
            col5:
              original.quantityType === updated.quantityType
                ? { op: op1, value: original.quantityType }
                : { op: op2, value: updated.quantityType },
          };
        },
      },
    };
    this.standard = {
      breadcrumb: ['Opening hours'],
      headers: ['Day of week', 'hours'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.weekdays },
            col1: { op: op, value: original.hours },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0:
              original.weekdays === updated.weekdays
                ? { op: op1, value: original.weekdays }
                : { op: op2, value: updated.weekdays },
            col1:
              original.hours === updated.hours ? { op: op1, value: original.hours } : { op: op2, value: updated.hours },
          };
        },
      },
    };
    this.exceptions = {
      breadcrumb: ['Holidays & Revised hours'],
      headers: ['date', 'hours', 'name'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          return {
            col0: { op: op, value: original.date },
            col1: { op: op, value: original.hours },
            col2: { op: op, value: original.name },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          return {
            col0: original.date === updated.date ? { op: op1, value: original.date } : { op: op2, value: updated.date },
            col1:
              original.hours === updated.hours ? { op: op1, value: original.hours } : { op: op2, value: updated.hours },
            col2: original.name === updated.name ? { op: op1, value: original.name } : { op: op2, value: updated.name },
          };
        },
      },
    };
    this.flexPays = {
      breadcrumb: ['Flexpay'],
      headers: function () {
        var headers;
        if (config.path === 'event') {
          headers = ['Name', 'Hours', 'Type'];
        } else if (config.path === 'eventTemplate') {
          headers = ['Name', 'Effective', 'Occurs', 'Hours', 'Type'];
        }
        return headers;
      },
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          var res,
            _original = config.flexpayFormatter(original);

          if (config.path === 'event') {
            res = {
              col0: { op: op, value: _original.name },
              col1: { op: op, value: _original.timeframe },
              col2: { op: op, value: _original.type },
            };
          } else if (config.path === 'eventTemplate') {
            _original = config.flexpayFormatter(original);
            res = {
              col0: { op: op, value: _original.name },
              col1: { op: op, value: _original.effectiveDates },
              col2: { op: op, value: _original.repeatCycle },
              col3: { op: op, value: _original.timeframe },
              col4: { op: op, value: _original.type },
            };
          }
          return res;
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          var res,
            _original = config.flexpayFormatter(original),
            _updated = config.flexpayFormatter(updated);
          if (config.path === 'event') {
            res = {
              col0:
                _original.name === _updated.name
                  ? { op: op1, value: _original.name }
                  : { op: op2, value: _updated.name },
              col1:
                _original.timeframe === _updated.timeframe
                  ? { op: op1, value: _original.timeframe }
                  : { op: op2, value: _updated.timeframe },
              col2:
                _original.type === _updated.type
                  ? { op: op1, value: _original.type }
                  : { op: op2, value: _updated.type },
            };
          } else if (config.path === 'eventTemplate') {
            res = {
              col0:
                _original.name === _updated.name
                  ? { op: op1, value: _original.name }
                  : { op: op2, value: _updated.name },
              col1:
                _original.effectiveDates === _updated.effectiveDates
                  ? { op: op1, value: _original.effectiveDates }
                  : { op: op2, value: _updated.effectiveDates },
              col2:
                _original.repeatCycle === _updated.repeatCycle
                  ? { op: op1, value: _original.repeatCycle }
                  : { op: op2, value: _updated.repeatCycle },
              col3:
                _original.timeframe === _updated.timeframe
                  ? { op: op1, value: _original.timeframe }
                  : { op: op2, value: _updated.timeframe },
              col4:
                _original.type === _updated.type
                  ? { op: op1, value: _original.type }
                  : { op: op2, value: _updated.type },
            };
          }
          return res;
        },
      },
    };
    this.resources = {
      breadcrumb: ['Assigned Resources'],
      headers: ['Time', 'Event Schedule', 'Assigned To', 'Quantity', 'Attributes'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          var _original = config.resourceRequestFormatter(original);
          return {
            col0: { op: op, value: _original.time },
            col1: { op: op, value: _original.event },
            col2: { op: op, value: _original.assigned },
            col3: { op: op, value: _original.quantity },
            col4: { op: op, value: _original.values },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          var _original = config.resourceRequestFormatter(original),
            _updated = config.resourceRequestFormatter(updated);
          return {
            col0:
              _original.time === _updated.time ? { op: op1, value: _original.time } : { op: op2, value: _updated.time },
            col1:
              _original.event === _updated.event
                ? { op: op1, value: _original.event }
                : { op: op2, value: _updated.event },
            col2:
              _original.assigned === _updated.assigned
                ? { op: op1, value: _original.assigned }
                : { op: op2, value: _updated.assigned },
            col3:
              _original.quantity === _updated.quantity
                ? { op: op1, value: _original.quantity }
                : { op: op2, value: _updated.quantity },
            col4:
              _original.values === _updated.values
                ? { op: op1, value: original.values }
                : { op: op2, value: _updated.values },
          };
        },
      },
    };
    this.resourceRequests = {
      breadcrumb: ['Event Template', 'Schedules', 'Resources'],
      headers: ['Category', 'Start Time', 'End Time', 'Resource Template', 'Attributes', 'Quantity', 'Resource'],
      accessor: null,
      handlers: {
        row: function (op: TODO, original: TODO) {
          var _original = config.eventResourceFormatter(original);
          return {
            col0: { op: op, value: _original.category },
            col1: { op: op, value: _original.startTime },
            col2: { op: op, value: _original.endTime },
            col3: { op: op, value: _original.resourceTemplate },
            col4: { op: op, value: _original.attributes },
            col5: { op: op, value: _original.quantity },
            col6: { op: op, value: _original.resource },
          };
        },
        rowByCondition: function (op1: TODO, op2: TODO, original: TODO, updated: TODO) {
          var _original = config.eventResourceFormatter(original),
            _updated = config.eventResourceFormatter(updated);
          return {
            col0:
              _original.category === _updated.category
                ? { op: op1, value: _original.category }
                : { op: op2, value: _updated.category },
            col1:
              _original.startTime === _updated.startTime
                ? { op: op1, value: _original.startTime }
                : { op: op2, value: _updated.startTime },
            col2:
              _original.endTime === _updated.endTime
                ? { op: op1, value: _original.endTime }
                : { op: op2, value: _updated.endTime },
            col3:
              _original.resourceTemplate === _updated.resourceTemplate
                ? { op: op1, value: _original.resourceTemplate }
                : { op: op2, value: _updated.resourceTemplate },
            col4:
              _original.attributes === _updated.attributes
                ? { op: op1, value: _original.attributes }
                : { op: op2, value: _updated.attributes },
            col5:
              _original.quantity === _updated.quantity
                ? { op: op1, value: _original.quantity }
                : { op: op2, value: _updated.quantity },
            col6:
              _original.resource === _updated.resource
                ? { op: op1, value: _original.resource }
                : { op: op2, value: _updated.resource },
          };
        },
      },
    };
  }
}

@Injectable({ providedIn: 'root' })
export class AuditTrailConfigsService {
  private versionHandler = inject(AuditTrailVersionHandlerService);
  private helpers = inject(HelpersService);
  private calendar = inject(CalendarService);
  private templates = inject(TemplatesService);
  private templatesHelpers = inject(TemplatesHelpersService);
  private flexpay = inject(FlexpayService);
  private resources = inject(ResourcesService);
  private $filter = inject<IFilterPort>(FILTER_PORT);

  gridList: string[] = [
    'priceList',
    'addOns',
    'schedules',
    'membership',
    'resources',
    'flexPays',
    'standard',
    'exceptions',
    'addOnsCombo',
  ];
  storage: Storage = {} as Storage;
  path: string = '';

  init(path: string): void {
    this.storage = new Storage(this);
    this.path = path;
  }

  buildGridOptions(columns: TODO[]): TODO {
    return {
      enableRowSelection: false,
      rowData: null,
      columnDefs: columns,
      rowSelection: 'single',
      paginate: false,
      multiSelect: false,
      rowHeight: 30,
      headerHeight: 30,
      instance: {},
    };
  }

  getCallbackHandlers(type: string): StorageType['handlers'] {
    return this.storage[type].handlers;
  }

  getGridBreadCrumbs(type: string): StorageType['breadcrumb'] {
    return this.storage[type].breadcrumb;
  }

  getAccessor(type: string): StorageType['accessor'] {
    return this.storage[type].accessor;
  }

  getGridHeaders(type: string): string[] | undefined {
    let res;
    if (typeof this.storage[type].headers === 'function') {
      res = this.storage[type].headers();
    } else {
      res = this.storage[type].headers;
    }
    return res;
  }

  // ─── Row formatters (legacy `helpers.*`) — called by the Storage handlers ────

  scheduleFormatter(obj: TODO, type: TODO): TODO {
    const dataset: TODO = {};
    const repeatTypes = this.templatesHelpers.getRepeatTypes();
    const version = this.versionHandler.getVersion();

    if (this.templates.hasSchedulePriceLists(obj)) {
      dataset.priceList = obj.priceLists[0].name;
    } else {
      dataset.priceList = 'No price list';
    }
    if (version[type].admissionType === 'generalAdmission') {
      dataset.startTime = this.$filter('date')(
        this.templates.hourToDate(obj.ranges[0].start),
        this.helpers.dateFormats.standardTime,
      );
    } else {
      dataset.startTime = this.$filter('date')(
        this.templates.hourToDate(obj.hours[0]),
        this.helpers.dateFormats.standardTime,
      );
    }
    dataset.name = obj.name;
    dataset.duration = this.templates.processScheduleDuration(obj);
    dataset.repeatCycle = this.templatesHelpers.getRepeatType(repeatTypes, obj.repeatCycle).label;
    dataset.salesChannels = this.templates.getSalesChannels(obj.salesChannels);
    dataset.effectiveDates = this.templatesHelpers.getEffectiveDates(obj.effectiveStartTime, obj.effectiveEndTime);

    return dataset;
  }

  flexpayFormatter(obj: TODO): TODO {
    let repeatTypes: TODO;
    let startTime: TODO;
    let endTime: TODO;
    const dataset: TODO = {};
    if (this.path === 'event') {
      dataset.timeframe =
        this.$filter('date')(obj.startTime, 'h:mma') + ' - ' + this.$filter('date')(obj.endTime, 'h:mma');
    } else if (this.path === 'eventTemplate') {
      repeatTypes = this.templatesHelpers.getRepeatTypes();
      // Legacy passed a 2nd format arg that hourToDate ignores — dropped for the 1-arg signature.
      startTime = this.templatesHelpers.hourToDate(obj.range.start);
      endTime = this.templatesHelpers.hourToDate(obj.range.end);

      dataset.effectiveDates = this.templatesHelpers.getEffectiveDates(obj.effectiveStartTime, obj.effectiveEndTime);
      dataset.repeatCycle = this.templatesHelpers.getRepeatType(repeatTypes, obj.repeatCycle).label;
      dataset.timeframe = this.$filter('date')(startTime, 'h:mma') + ' - ' + this.$filter('date')(endTime, 'h:mma');
    }
    dataset.name = obj.name;
    dataset.type = this.flexpay.getFlexpayType(this.flexpay.types, obj.type).label;

    return dataset;
  }

  eventResourceFormatter(obj: TODO): TODO {
    let dataset: TODO = {};
    this.resources.setSchedule(obj);
    dataset = this.resources.processRequest(obj);

    return dataset;
  }

  resourceRequestFormatter(obj: TODO): TODO {
    const data: TODO = {};
    const version = this.versionHandler.getVersion();
    const startTime = this.calendar.parseDate(obj.resourceStartTime);
    const endTime = this.calendar.parseDate(obj.resourceEndTime);

    data.time = this.$filter('date')(this.calendar.parseDate(startTime), this.helpers.dateFormats.shortDateTime);

    if (endTime) {
      data.time += ' - ';
      data.time += this.$filter('date')(this.calendar.parseDate(endTime), this.helpers.dateFormats.shortDateTime);
    } else {
      data.time += ' - Indefinite';
    }
    data.request = obj.type;
    data.event = version.updated.name;
    data.assigned = obj.resourceName;
    data.quantity = obj.quantity;
    data.attributes = obj.values || '';

    return data;
  }
}
